import styled from 'styled-components'
import SVG from 'react-inlinesvg'

type SearchResultIconProps = {
  product: any
  categoryTitle?: string
  dataTestId?: string
}

const Icon = styled(SVG)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  path,
  circle,
  rect {
    fill: ${({theme}) => theme.accent[100]};
  }
`

const iconSources: Record<string, any> = {
  api: require('./icons/api.svg'),
  'code-square-bold': require('./icons/code-square-bold.svg'),
  'archive-minimalistic-bold': require('./icons/archive-minimalistic-bold.svg'),
  'hand-money-bold': require('./icons/hand-money-bold.svg'),
  'shield-check-bold': require('./icons/shield-check-bold.svg'),
  'reorder-bold': require('./icons/reorder-bold.svg'),
  'key-minimalistic-square-bold': require('./icons/key-minimalistic-square-bold.svg'),
  'shield-user-bold': require('./icons/shield-user-bold.svg'),
  'window-frame-bold': require('./icons/window-frame-bold.svg'),
  'trash-bin-2-bold': require('./icons/trash-bin-2-bold.svg'),
  'card-search-bold': require('./icons/card-search-bold.svg'),
  'box-minimalistic-bold': require('./icons/box-minimalistic-bold.svg'),
  'traffic-economy-bold': require('./icons/traffic-economy-bold.svg'),
  'document-add-bold': require('./icons/document-add-bold.svg'),
  'widget-add-bold': require('./icons/widget-add-bold.svg'),
  'clipboard-text-bold': require('./icons/clipboard-text-bold.svg'),
  'notes-bold': require('./icons/notes-bold.svg'),
  'settings-bold': require('./icons/settings-bold.svg'),
  'flip-horizontal-bold': require('./icons/flip-horizontal-bold.svg'),
  'wallet-money-bold': require('./icons/wallet-money-bold.svg'),
  'notebook-bold': require('./icons/notebook-bold.svg'),
  'server-bold': require('./icons/server-bold.svg'),
  'copy-bold': require('./icons/copy-bold.svg'),
  'magnifer-bold': require('./icons/magnifer-bold.svg'),
  'upload-bold': require('./icons/upload-bold.svg'),
  'sidebar-code-bold': require('./icons/sidebar-code-bold.svg'),
  'chart-2-bold': require('./icons/chart-2-bold.svg'),
  'global-bold': require('./icons/global-bold.svg'),
  'revote-bold': require('./icons/revote-bold.svg'),
  'exit-bold': require('./icons/exit-bold.svg'),
  'stop-circle-bold': require('./icons/stop-circle-bold.svg'),
  'play-circle-bold': require('./icons/play-circle-bold.svg'),
  'library-bold': require('./icons/library-bold.svg'),
  'full-screen-square-bold': require('./icons/full-screen-square-bold.svg'),
  'map-point-remove-bold': require('./icons/map-point-remove-bold.svg'),
  'info-circle-bold': require('./icons/info-circle-bold.svg'),
  'structure-bold': require('./icons/structure-bold.svg'),
  'command-bold': require('./icons/command-bold.svg'),
  'shield-bold': require('./icons/shield-bold.svg'),
  'scale-bold': require('./icons/scale-bold.svg'),
  'wi-fi-router-bold': require('./icons/wi-fi-router-bold.svg'),
  'document-bold': require('./icons/document-bold.svg'),
  windows: require('./icons/windows.svg'),
  linux: require('./icons/linux.svg'),
}

const iconRules: Array<{ icon: string; patterns: RegExp[] }> = [
  { icon: 'api', patterns: [/^api(?:\s|$)/, /\/category\/api/] },
  { icon: 'archive-minimalistic-bold', patterns: [/правила користування|terms|service rules|архівувати|archive|shelve/] },
  { icon: 'hand-money-bold', patterns: [/політика тарифікації|charging policy|payment policy/] },
  { icon: 'shield-check-bold', patterns: [/політика конфіденційності|privacy|права доступу|permissions|shield-check/] },
  { icon: 'reorder-bold', patterns: [/перенести послуги|transfer services|reorder/] },
  { icon: 'key-minimalistic-square-bold', patterns: [/openapi токен|openstack токен|openapi token|openstack token|ssh-?ключ|token/] },
  { icon: 'shield-user-bold', patterns: [/відновлення доступу|restore access|access recovery/] },
  { icon: 'window-frame-bold', patterns: [/реєстрація|registration|консоль сервера|server console|window frame/] },
  { icon: 'trash-bin-2-bold', patterns: [/видал|delete|trash/] },
  { icon: 'card-search-bold', patterns: [/автоплат|automatic payments|card search/] },
  { icon: 'box-minimalistic-bold', patterns: [/terraform|3d cube|convert-3d-cube/] },
  { icon: 'traffic-economy-bold', patterns: [/додати правило|add rule|призначити floating ip|assign floating ip|traffic economy/] },
  { icon: 'document-add-bold', patterns: [/редаг|rename|change instance type|document add/] },
  { icon: 'widget-add-bold', patterns: [/створ|create|widget add/] },
  { icon: 'clipboard-text-bold', patterns: [/загальна інформація|general information|clipboard text/] },
  { icon: 'notes-bold', patterns: [/перегляд списку|список floating ip|view .*list|list of|note$/] },
  { icon: 'settings-bold', patterns: [/управління|management|settings|конфігурації|configuration|взаємодія/] },
  { icon: 'flip-horizontal-bold', patterns: [/від'єднання floating ip|detach floating ip|flip horizontal/] },
  { icon: 'wallet-money-bold', patterns: [/оплата|payment|wallet|billing summary/] },
  { icon: 'notebook-bold', patterns: [/квоти|quotas|notebook/] },
  { icon: 'server-bold', patterns: [/мережеві диски|локальні диски|network volumes|local volumes|object storage|bucket|storage/] },
  { icon: 'copy-bold', patterns: [/резервн|backup|copy/] },
  { icon: 'magnifer-bold', patterns: [/діагностика|diagnostics?/] },
  { icon: 'windows', patterns: [/windows/] },
  { icon: 'linux', patterns: [/linux/] },
  { icon: 'upload-bold', patterns: [/завантаження|експорт|export|upload/] },
  { icon: 'sidebar-code-bold', patterns: [/консоль$|console$|sidebar code/] },
  { icon: 'chart-2-bold', patterns: [/метрик|traffic data|chart|metrics?/] },
  { icon: 'global-bold', patterns: [/zones|dns|global/] },
  { icon: 'revote-bold', patterns: [/відновити|перезавантажити|restart|reboot|rescue|revote/] },
  { icon: 'exit-bold', patterns: [/вийти з режиму|exit/] },
  { icon: 'stop-circle-bold', patterns: [/зупинити|stop/] },
  { icon: 'play-circle-bold', patterns: [/запустити|start|play/] },
  { icon: 'library-bold', patterns: [/перевстановлення|reinstall|library/] },
  { icon: 'full-screen-square-bold', patterns: [/змінити розмір|resize|full screen/] },
  { icon: 'map-point-remove-bold', patterns: [/від’єднати ip|detach ip|map point remove/] },
  { icon: 'info-circle-bold', patterns: [/статус|status|info-circle/] },
  { icon: 'structure-bold', patterns: [/підмережі|subnet|structure/] },
  { icon: 'command-bold', patterns: [/клієнт сервера|server client|command/] },
  { icon: 'shield-bold', patterns: [/security groups?|групи безпеки|shield/] },
  { icon: 'scale-bold', patterns: [/масштабування|scale/] },
  { icon: 'wi-fi-router-bold', patterns: [/маршрутизатор|routers?|wi-fi router/] },
]

const getIconName = (product:any, categoryTitle?: string) => {
  const source = [
    product?.title,
    categoryTitle,
    product?.url,
    product?.url_without_anchor,
    product?.type,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return iconRules.find(({ patterns }) => patterns.some((pattern) => pattern.test(source)))?.icon || 'document-bold'
}

const getIconSource = (iconName: string) => {
  const icon = iconSources[iconName] || iconSources['document-bold']

  return typeof icon === 'string' ? icon : icon?.default
}

const SearchResultIcon = ({ product, categoryTitle, dataTestId }: SearchResultIconProps) => {
  const iconName = getIconName(product, categoryTitle)

  return (
    <Icon
      data-test-id={dataTestId}
      src={getIconSource(iconName)}
      aria-hidden="true"
    />
  )
}

export default SearchResultIcon
